import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ApiError } from '../services/api';
import { promotionsApi } from '../services/promotionsApi';
import { PromoCodeDetail } from '../types/promotions';
import TypedConfirmDialog from '../components/TypedConfirmDialog';
import PromoCodeForm from '../components/promotions/PromoCodeForm';
import ShareModal from '../components/promotions/ShareModal';
import HoldersTable from '../components/promotions/HoldersTable';
import PromoStatusChip, { AudienceChip } from '../components/promotions/PromoStatusChip';
import { PromoSummaryCards, PromoTermsGrid } from '../components/promotions/PromoSummaryCards';
import { ToastView, useToast } from '../components/promotions/useToast';
import { shareBlockedReason } from '../components/promotions/promoFormat';

type Dialog = null | 'edit' | 'share' | 'disable';

/** Spec 059 Flow B/C/D — one code: terms, performance, holders; Share · Edit · Disable/Enable · Copy link. */
export default function PromotionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialToast = (location.state as { toast?: string } | null)?.toast;
  const { toast, show } = useToast(initialToast ? { message: initialToast, type: 'success' } : null);
  const [promo, setPromo] = useState<PromoCodeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [holdersKey, setHoldersKey] = useState(0);

  const load = useCallback(async (quiet = false) => {
    if (!id || !auth.token) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      setPromo(await promotionsApi.get(id, auth.token));
    } catch (err) {
      setNotFound(err instanceof ApiError && err.status === 404);
      setError(err instanceof Error ? err.message : 'Failed to load the code');
    } finally {
      setLoading(false);
    }
  }, [id, auth.token]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (enable: boolean) => {
    if (!promo || !auth.token) return;
    setBusy(true);
    try {
      const saved = enable ? await promotionsApi.enable(promo.id, auth.token) : await promotionsApi.disable(promo.id, auth.token);
      setDialog(null);
      if (saved && typeof saved === 'object' && 'code' in saved) setPromo(saved); else await load(true);
      show(enable ? `${promo.code} enabled` : `${promo.code} disabled`);
    } catch (err) {
      show(err instanceof Error ? err.message : 'Action failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!promo) return;
    try {
      await navigator.clipboard.writeText(promo.shareLink);
      show('Link copied');
    } catch {
      show(`Couldn't copy — the link is ${promo.shareLink}`, 'error');
    }
  };

  if (loading) {
    return <div className="admin-loading" data-testid="promotion-loading"><div className="admin-loading-spinner" /></div>;
  }
  if (error || !promo) {
    return (
      <div className="admin-card" data-testid="promotion-error">
        <div className="admin-info-banner danger" role="alert">{notFound ? 'Promo code not found.' : error ?? 'Promo code not found.'}</div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!notFound && <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => load()}>Retry</button>}
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => navigate('/promotions')}>Back to promo codes</button>
        </div>
      </div>
    );
  }

  const disabled = promo.derivedStatus === 'DISABLED';
  const blocked = shareBlockedReason(promo);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <button type="button" onClick={() => navigate('/promotions')} className="admin-btn admin-btn-outline admin-btn-sm">Back</button>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>{promo.code}</h2>
        <PromoStatusChip status={promo.derivedStatus} />
        <AudienceChip audience={promo.audience} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="admin-btn admin-btn-primary admin-btn-sm" disabled={!!blocked} title={blocked ?? undefined} onClick={() => setDialog('share')}>Share</button>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setDialog('edit')}>Edit</button>
          {disabled ? (
            <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={busy}
              onClick={() => { if (window.confirm(`Enable ${promo.code}? It becomes claimable and usable again within its window and limits.`)) toggleEnabled(true); }}>
              Enable
            </button>
          ) : (
            <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" disabled={busy} onClick={() => setDialog('disable')}>Disable</button>
          )}
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={copyLink}>Copy link</button>
        </div>
      </div>

      <PromoSummaryCards promo={promo} />
      <PromoTermsGrid promo={promo} />
      <HoldersTable promoId={promo.id} code={promo.code} token={auth.token as string} refreshKey={holdersKey}
        onChanged={() => load(true)} notify={show} onShare={blocked ? undefined : () => setDialog('share')} />

      {dialog === 'edit' && (
        <PromoCodeForm promo={promo} token={auth.token as string} onClose={() => setDialog(null)}
          onSaved={(saved) => { setPromo(saved); setDialog(null); show('Changes saved'); }}
          onReload={() => { setDialog(null); load(); }} />
      )}
      {dialog === 'share' && (
        <ShareModal promo={promo} token={auth.token as string}
          onClose={() => setDialog(null)}
          onShared={(summary) => { show(summary); setHoldersKey((k) => k + 1); load(true); }} />
      )}
      {dialog === 'disable' && (
        <TypedConfirmDialog
          title={`Disable ${promo.code}?`}
          message="Nobody can claim or apply it while disabled. Bookings already holding a use keep it and can still be paid. You can enable it again later."
          confirmPhrase={promo.code}
          confirmLabel={busy ? 'Disabling…' : 'Disable code'}
          danger
          onConfirm={() => toggleEnabled(false)}
          onCancel={() => setDialog(null)}
        />
      )}
      <ToastView toast={toast} />
    </div>
  );
}
